import type { JSX } from 'react';
import { useState } from 'react';
import type { SurfaceOf } from './types';
import { useSurfaceActions } from './context';
import { useT } from '../i18n/useT';
import type { StringKey } from '../i18n/strings';

type Clarification = SurfaceOf<'clarification'>;

const REASON_KEY: Partial<Record<Clarification['reason'], StringKey>> = {
  TRANSCRIPTION: 'clarificationTranscription',
  INTERPRETATION: 'clarificationInterpretation',
  AMBIGUOUS_MATCH: 'clarificationAmbiguous',
  MISSING_PARAMETER: 'clarificationMissing',
  VALIDATION: 'clarificationValidation',
  CONFIRMATION: 'clarificationConfirmation',
};

export function ClarificationSurfaceView({ surface }: { surface: Clarification }): JSX.Element {
  const t = useT();
  const actions = useSurfaceActions();
  const [chosen, setChosen] = useState<string | undefined>();
  const [typed, setTyped] = useState('');
  const done = chosen !== undefined;
  return (
    <section className={`surface surface-clarify reason-${surface.reason.toLowerCase()}`} role="group" aria-label={surface.question}>
      <span className="badge">{t((REASON_KEY[surface.reason] ?? 'clarificationMissing'))}</span>
      <p className="clarify-question">{surface.question}</p>
      {surface.options.length > 0 ? (
        <div className="option-list">
          {surface.options.map((o) => (
            <button
              key={o.id}
              type="button"
              className={chosen === o.id ? 'btn option selected' : 'btn option'}
              disabled={done}
              onClick={() => {
                setChosen(o.id);
                actions.sendMessage(o.reply);
              }}
            >
              <span>{o.label}</span>
              {o.detail ? <span className="muted small">{o.detail}</span> : null}
            </button>
          ))}
        </div>
      ) : null}
      {!done ? (
        <form
          className="clarify-type"
          onSubmit={(e) => {
            e.preventDefault();
            const text = typed.trim();
            if (!text) return;
            setChosen('__typed');
            actions.sendMessage(text);
          }}
        >
          <input type="text" value={typed} onChange={(e) => setTyped(e.target.value)} placeholder="…or type your answer" aria-label="Type your answer" />
          <button type="submit" className="btn btn-small" disabled={!typed.trim()}>
            {t('send')}
          </button>
        </form>
      ) : null}
    </section>
  );
}
